import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import T from '../common/T';
import './CallAudioOutput.css';

// Read only Twilio's public AudioHelper API. Device labels/IDs stay in this UI.
const readOutputs = (audio) => {
  if (!audio || audio.isOutputSelectionSupported !== true || typeof audio.speakerDevices?.set !== 'function') {
    return { supported: false, outputs: [], selected: '' };
  }
  try {
    const outputs = [];
    const seen = new Set();
    for (const device of audio.availableOutputDevices?.values?.() || []) {
      if (typeof device.deviceId !== 'string' || !device.deviceId || seen.has(device.deviceId)) continue;
      seen.add(device.deviceId);
      outputs.push({ id: device.deviceId, label: String(device.label || 'Audioausgang ' + (outputs.length + 1)).slice(0, 160) });
    }
    const active = [...(audio.speakerDevices.get?.() || [])];
    const selected = active.length === 1 && seen.has(active[0].deviceId) ? active[0].deviceId : '';
    return { supported: true, outputs, selected };
  } catch (_) {
    return { supported: false, outputs: [], selected: '' };
  }
};

export default function CallAudioOutput({ audio = null, tone = 'light' }) {
  const [state, setState] = useState(() => readOutputs(audio));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const generation = useRef(0);
  const pending = useRef(false);

  useEffect(() => {
    const version = ++generation.current;
    pending.current = false;
    setBusy(false);
    setError('');
    const refresh = () => {
      if (generation.current === version) setState(readOutputs(audio));
    };
    refresh();
    audio?.on?.('deviceChange', refresh);
    return () => {
      generation.current += 1;
      if (audio?.off) audio.off('deviceChange', refresh);
      else audio?.removeListener?.('deviceChange', refresh);
    };
  }, [audio]);

  const choose = async (event) => {
    const id = event.target.value;
    if (pending.current || !state.outputs.some((item) => item.id === id)) return;
    const version = generation.current;
    pending.current = true;
    setBusy(true);
    setError('');
    try {
      // Change call playback only. Never mute/reacquire the microphone or change the ringtone.
      await audio.speakerDevices.set([id]);
      if (generation.current !== version) return;
      const next = readOutputs(audio);
      setState(next);
      if (next.selected !== id) setError('Der gewählte Ausgang wurde nicht bestätigt. Bitte erneut wählen.');
    } catch (_) {
      if (generation.current !== version) return;
      setState(readOutputs(audio));
      setError('Audioausgang konnte nicht gewechselt werden. Bitte die aktuelle Ausgabe prüfen.');
    } finally {
      if (generation.current === version) { pending.current = false; setBusy(false); }
    }
  };

  return <div className={'call-audio-output call-audio-output--' + tone}>
    <label>
      <span><T>Audioausgabe</T></span>
      {state.supported && state.outputs.length > 0
        ? <select aria-label="Audioausgabe" value={state.selected} onChange={choose}
            disabled={busy || state.outputs.length < 2} aria-busy={busy}>
          {!state.selected && <option value="" disabled><T>Vom System gewählter Ausgang</T></option>}
          {state.outputs.map((output) => <option key={output.id} value={output.id}>{output.label}</option>)}
        </select>
        : <span className="call-audio-output__system" role="status"><T>Audioausgabe vom System gewählt</T></span>}
    </label>
    <p className="call-audio-output__hint">
      <T>{busy ? 'Audioausgang wird gewechselt…'
        : !state.supported || state.outputs.length < 2
          ? 'Dieser Browser bietet hier keine umschaltbaren Audioausgänge an. Eine Hörmuschel-Auswahl lässt sich deshalb im ERP nicht erzwingen.'
          : 'Wähle Hörmuschel, Lautsprecher oder Headset, sofern dein Gerät sie hier anbietet. Die Mikrofon-Stummschaltung bleibt unverändert.'}</T>
    </p>
    {error && <p className="call-audio-output__error" role="alert"><T>{error}</T></p>}
  </div>;
}

CallAudioOutput.propTypes = {
  audio: PropTypes.shape({
    isOutputSelectionSupported: PropTypes.bool,
    availableOutputDevices: PropTypes.object,
    speakerDevices: PropTypes.shape({ get: PropTypes.func, set: PropTypes.func }),
    on: PropTypes.func, off: PropTypes.func, removeListener: PropTypes.func,
  }),
  tone: PropTypes.oneOf(['light', 'dark']),
};
